// Helper around the /wizard-files staging endpoints.
//
// Uploads return a row object the FE can keep in its draft payload (only the
// id + metadata is JSON-safe — the actual File object stays in the browser
// only until the upload completes). On wizard hydration the FE re-reads the
// list and uses the existing rows; users never need to re-upload after a
// page refresh.

import { api, ApiError } from './api.js';

/**
 * Stage a file under (kind, slot). Returns the persisted row:
 *   { id, kind, slot, original_name, mime, size_bytes, extracted_fields, ... }
 */
export async function stageWizardFile(kind, slot, file) {
  if (!(file instanceof File) && !(file instanceof Blob)) {
    throw new TypeError('stageWizardFile expects a File/Blob');
  }
  const fd = new FormData();
  // Preserve the original filename when given a Blob without one.
  const name = (file instanceof File && file.name) ? file.name : 'upload.bin';
  fd.append('file', file, name);
  const res = await api.post(
    `/wizard-files/${encodeURIComponent(kind)}/${encodeURIComponent(slot)}`,
    fd,
  );
  return res?.data || null;
}

/** List active (non-promoted) staged files for the current user + kind. */
export async function listWizardFiles(kind, slot = null) {
  const q = slot ? `?slot=${encodeURIComponent(slot)}` : '';
  const res = await api.get(`/wizard-files/${encodeURIComponent(kind)}${q}`);
  return Array.isArray(res?.data) ? res.data : [];
}

/**
 * Attach (or replace) extracted fields on a staged file. Used by the wizard
 * after running OCR against the freshly uploaded bytes so the extraction
 * survives a refresh together with the file row.
 */
export async function setWizardFileExtractedFields(id, fields) {
  const res = await api.patch(
    `/wizard-files/${encodeURIComponent(id)}/extracted-fields`,
    { fields: fields || {} },
  );
  return res?.data || null;
}

export async function deleteWizardFile(id) {
  try {
    await api.delete(`/wizard-files/${encodeURIComponent(id)}`);
    return true;
  } catch (err) {
    // 404 = already gone — treat as success so the UI doesn't trip.
    if (err instanceof ApiError && err.status === 404) return true;
    throw err;
  }
}
